import React, { MouseEvent, useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { EditOutlined, MoreHoriz } from "@mui/icons-material";
import { observer } from "mobx-react";
import {
  formatSwayyCouponDiscount,
  formatSwayyCouponUsage,
  formatSwayyCouponDate,
  getSwayyCouponDisplayState,
  isSwayyCouponDateElapsed,
  SwayyCoupon,
} from "../../../../../models/SwayyCoupon/SwayyCoupon";
import {
  getSwayyCouponChannelLabel,
  getSwayyCouponStatusLabel,
  getSwayyCouponStatusTone,
} from "../../../../../models/SwayyCoupon/SwayyCouponFilters";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import {
  formatDate,
} from "../../../../../views/components/common/functions/formatDate";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import { swayyCouponStore } from "../../../stores/SwayyCouponStore";
import { SwayyTableEmptyState } from "../SwayyPageShell";
import {
  swayyTableBodyCellLeftSx,
  swayyTableBodyCellSx,
  swayyTableHeadCellLeftSx,
  swayyTableHeadCellSx,
  swayyTableHeadRowSx,
} from "../swayyTableStyles";

const PAGE_SIZE_OPTIONS: FilterValue[] = [
  { id: "10", value: "10 / page" },
  { id: "20", value: "20 / page" },
  { id: "50", value: "50 / page" },
  { id: "100", value: "100 / page" },
];

const thSx = swayyTableHeadCellSx;
const thLeftSx = swayyTableHeadCellLeftSx;
const tdSx = swayyTableBodyCellSx;
const tdLeftSx = swayyTableBodyCellLeftSx;

const checkboxCellSx = {
  ...thLeftSx,
  width: 32,
  minWidth: 32,
  px: 0.75,
};

const checkboxBodyCellSx = {
  ...tdLeftSx,
  width: 32,
  minWidth: 32,
  px: 0.75,
};

const checkboxSx = {
  color: swayyColors.pine,
  p: 0.25,
  "& .MuiSvgIcon-root": { fontSize: 16 },
  "&.Mui-checked": { color: swayyColors.pine },
  "&.MuiCheckbox-indeterminate": { color: swayyColors.pine },
} as const;

const couponCodeSx = {
  fontFamily: swayyFont,
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  cursor: "pointer",
  "&:hover": { color: swayyColors.pine },
};

const elapsedChipSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  px: 1.25,
  py: 0.35,
  borderRadius: swayyRadii.full,
  bgcolor: "rgba(220, 38, 38, 0.1)",
  color: swayyColors.danger,
  fontSize: 11,
  fontWeight: 700,
  fontFamily: swayyFont,
  whiteSpace: "nowrap",
} as const;

const ElapsedChip = ({ label }: { label: string }) => (
  <Box component="span" sx={elapsedChipSx}>
    {label}
  </Box>
);

const CouponDateValue = ({ value }: { value: string | null }) => {
  if (!value) return <>{"—"}</>;
  const label = formatSwayyCouponDate(value);
  if (!isSwayyCouponDateElapsed(value)) return <>{label}</>;
  return <ElapsedChip label={label} />;
};

const ExpiryBadge = ({ isExpired }: { isExpired: boolean }) => {
  if (!isExpired) return <>{"—"}</>;
  return <ElapsedChip label="Expired" />;
};

const StatusBadge = ({ status }: { status: string }) => {
  const tone = getSwayyCouponStatusTone(status);
  const styles: Record<string, { bg: string; color: string }> = {
    pine: { bg: swayyColors.pineLight, color: swayyColors.deepGreen },
    success: { bg: "rgba(30, 166, 114, 0.12)", color: swayyColors.success },
    warning: { bg: "rgba(217, 119, 6, 0.12)", color: swayyColors.warning },
    danger: { bg: "rgba(220, 38, 38, 0.1)", color: swayyColors.danger },
    muted: { bg: swayyColors.pageBg, color: swayyColors.textSecondary },
  };
  const toneStyle = styles[tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.25,
        py: 0.35,
        borderRadius: swayyRadii.full,
        bgcolor: toneStyle.bg,
        color: toneStyle.color,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: swayyFont,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {getSwayyCouponStatusLabel(status)}
    </Box>
  );
};

interface SwayyCouponsTableProps {
  onView: (coupon: SwayyCoupon) => void;
  onEdit?: (coupon: SwayyCoupon) => void;
  onRetry?: () => void;
  selectable?: boolean;
  canEdit?: boolean;
}

const CouponRowActionMenu = ({
  coupon,
  onEdit,
}: {
  coupon: SwayyCoupon;
  onEdit: (coupon: SwayyCoupon) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Open actions for ${coupon.code}`}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        onClick={openMenu}
        sx={{
          color: swayyColors.deepGreen,
          border: `1px solid ${swayyColors.border}`,
          borderRadius: swayyRadii.sm,
          "&:hover": { backgroundColor: swayyColors.pineLight },
        }}
      >
        <MoreHoriz fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        onClick={(event) => event.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            onEdit(coupon);
          }}
        >
          <ListItemIcon
            sx={{
              color: swayyColors.textSecondary,
              "& .MuiSvgIcon-root": { fontSize: 18 },
            }}
          >
            <EditOutlined />
          </ListItemIcon>
          <CustomTypography variant="body2">Edit</CustomTypography>
        </MenuItem>
      </Menu>
    </>
  );
};

const SwayyCouponsTable: React.FC<SwayyCouponsTableProps> = ({
  onView,
  onEdit,
  onRetry,
  selectable = false,
  canEdit = false,
}) => {
  const store = swayyCouponStore;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const total = store.pagination.total || 0;
  const page = store.params.page;
  const limit = store.params.limit || 20;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const pageCount = Math.max(1, Math.ceil(total / limit) || 1);
  const showActions = canEdit && typeof onEdit === "function";
  const columnCount = (selectable ? 10 : 9) + (showActions ? 1 : 0);

  const paginationFooter = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${swayyColors.border}`,
      }}
    >
      <CustomTypography
        variant="body3"
        color={swayyColors.textSecondary}
        sx={{ fontFamily: swayyFont, fontSize: 12 }}
      >
        Showing {from}–{to} of {total} coupons
        {selectable && store.selectedCount > 0
          ? ` · ${store.selectedCount} selected`
          : ""}
      </CustomTypography>

      <Pagination
        count={pageCount}
        page={page}
        onChange={(_, next) => store.handlePageChange(next)}
        shape="rounded"
        size="small"
        sx={{
          "& .MuiPaginationItem-root": {
            fontFamily: swayyFont,
            color: swayyColors.deepGreen,
            borderRadius: swayyRadii.sm,
          },
          "& .Mui-selected": {
            bgcolor: `${swayyColors.pine} !important`,
            color: `${swayyColors.white} !important`,
            "&:hover": { bgcolor: `${swayyColors.pineHover} !important` },
          },
        }}
      />

      <Box sx={{ minWidth: 120 }}>
        <AppSearchDropdownMulti
          title="Rows"
          isMultiSelect={false}
          list={PAGE_SIZE_OPTIONS}
          value={PAGE_SIZE_OPTIONS.filter(
            (option) => option.id === String(limit),
          )}
          local
          fontSize="12px"
          minWidth="120px"
          handleSelect={(items) => {
            store.handleRowsPerPageChange(Number(items[0]?.id || 20));
          }}
        />
      </Box>
    </Box>
  );

  const renderEmpty = () => (
    <SwayyTableEmptyState
      title="No coupons found"
      description="No coupons match these filters. Try adjusting search or filters."
    />
  );

  if (!store.loading && store.error && store.coupons.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: swayyRadii.lg,
          border: `1px solid ${swayyColors.border}`,
          overflow: "hidden",
          bgcolor: swayyColors.white,
        }}
      >
        <SwayyTableEmptyState
          title="Couldn't load coupons"
          description={store.error}
          actionLabel="Try again"
          onAction={onRetry}
          danger
        />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: swayyRadii.lg,
        border: `1px solid ${swayyColors.border}`,
        overflow: "hidden",
        bgcolor: swayyColors.white,
      }}
    >
      {isMobile ? (
        <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
          {!store.loading && store.coupons.length > 0 && selectable ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 0.5,
              }}
            >
              <Checkbox
                checked={store.allPageSelected}
                indeterminate={store.somePageSelected}
                onChange={store.toggleSelectAllOnPage}
                size="small"
                sx={checkboxSx}
                inputProps={{ "aria-label": "Select all coupons on this page" }}
              />
              <CustomTypography
                variant="body3"
                color={swayyColors.textSecondary}
                sx={{ fontFamily: swayyFont, fontSize: 12 }}
              >
                {store.selectedCount > 0
                  ? `${store.selectedCount} selected`
                  : "Select all"}
              </CustomTypography>
            </Box>
          ) : null}
          {store.loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  height={110}
                  sx={{ borderRadius: swayyRadii.md }}
                />
              ))
            : store.coupons.length === 0
              ? renderEmpty()
              : store.coupons.map((coupon) => (
                  <Box
                    key={coupon.id}
                    sx={{
                      p: 1.75,
                      borderRadius: swayyRadii.md,
                      border: `1px solid ${
                        selectable && store.isSelected(coupon.id)
                          ? swayyColors.pine
                          : swayyColors.border
                      }`,
                      bgcolor:
                        selectable && store.isSelected(coupon.id)
                          ? swayyColors.pineLight
                          : swayyColors.pageBg,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 0.75,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {selectable ? (
                          <Checkbox
                            checked={store.isSelected(coupon.id)}
                            onChange={() => store.toggleSelected(coupon.id)}
                            onClick={(event) => event.stopPropagation()}
                            size="small"
                            sx={checkboxSx}
                            inputProps={{ "aria-label": `Select ${coupon.code}` }}
                          />
                        ) : null}
                        <Box
                          onClick={() => onView(coupon)}
                          sx={{ cursor: "pointer", maxWidth: "100%", minWidth: 0 }}
                        >
                          <SkuCopyRow
                            sku={coupon.code}
                            emptyFallback="—"
                            copyLabel="Copy coupon code"
                            color={swayyColors.deepGreen}
                            fontWeight={700}
                            typographySx={{ ...couponCodeSx, fontSize: 14 }}
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 0.5,
                          flexShrink: 0,
                        }}
                      >
                        {showActions && onEdit ? (
                          <CouponRowActionMenu
                            coupon={coupon}
                            onEdit={onEdit}
                          />
                        ) : null}
                        <StatusBadge
                          status={getSwayyCouponDisplayState(coupon)}
                        />
                        <ExpiryBadge isExpired={coupon.is_expired} />
                      </Box>
                    </Box>
                    <CustomTypography
                      variant="body3"
                      color={swayyColors.deepGreen}
                      sx={{
                        fontFamily: swayyFont,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {formatSwayyCouponDiscount(coupon)}
                    </CustomTypography>
                    <CustomTypography
                      variant="body3"
                      color={swayyColors.textSecondary}
                      sx={{ fontFamily: swayyFont, fontSize: 11, mt: 0.2 }}
                    >
                      {getSwayyCouponChannelLabel(coupon.channel)} ·{" "}
                      {formatSwayyCouponUsage(coupon)}
                    </CustomTypography>
                    <CustomTypography
                      variant="body3"
                      color={swayyColors.textSecondary}
                      sx={{ fontFamily: swayyFont, fontSize: 11, mt: 0.5 }}
                    >
                      Created{" "}
                      {coupon.created_at ? formatDate(coupon.created_at) : "—"}
                    </CustomTypography>
                  </Box>
                ))}
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={swayyTableHeadRowSx}>
                {selectable ? (
                  <TableCell sx={checkboxCellSx}>
                    <Checkbox
                      checked={store.allPageSelected}
                      indeterminate={store.somePageSelected}
                      disabled={store.loading || store.coupons.length === 0}
                      onChange={store.toggleSelectAllOnPage}
                      size="small"
                      sx={checkboxSx}
                      inputProps={{ "aria-label": "Select all coupons on this page" }}
                    />
                  </TableCell>
                ) : null}
                <TableCell sx={thLeftSx}>Coupon</TableCell>
                <TableCell sx={thSx}>Discount</TableCell>
                <TableCell sx={thSx}>Channel</TableCell>
                <TableCell sx={thSx}>Usage</TableCell>
                <TableCell sx={thSx}>Status</TableCell>
                <TableCell sx={thSx}>Expired</TableCell>
                <TableCell sx={thSx}>Starts</TableCell>
                <TableCell sx={thSx}>Ends</TableCell>
                <TableCell sx={thSx}>Created</TableCell>
                {showActions ? <TableCell sx={thSx}>Action</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {store.loading
                ? Array.from({ length: 6 }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: columnCount }).map((__, cell) => (
                        <TableCell
                          key={cell}
                          sx={
                            selectable && cell === 0
                              ? checkboxBodyCellSx
                              : cell === (selectable ? 1 : 0)
                                ? tdLeftSx
                                : tdSx
                          }
                        >
                          <Skeleton height={22} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : store.coupons.length === 0
                  ? (
                      <TableRow>
                        <TableCell colSpan={columnCount} sx={{ border: 0 }}>
                          {renderEmpty()}
                        </TableCell>
                      </TableRow>
                    )
                  : store.coupons.map((coupon) => (
                      <TableRow
                        key={coupon.id}
                        hover
                        sx={{
                          bgcolor: selectable && store.isSelected(coupon.id)
                            ? swayyColors.pineLight
                            : undefined,
                          "&:hover": { bgcolor: swayyColors.pineLight },
                        }}
                      >
                        {selectable ? (
                          <TableCell sx={checkboxBodyCellSx}>
                            <Checkbox
                              checked={store.isSelected(coupon.id)}
                              onChange={() => store.toggleSelected(coupon.id)}
                              onClick={(event) => event.stopPropagation()}
                              size="small"
                              sx={checkboxSx}
                              inputProps={{
                                "aria-label": `Select ${coupon.code}`,
                              }}
                            />
                          </TableCell>
                        ) : null}
                        <TableCell sx={tdLeftSx}>
                          <Box
                            onClick={() => onView(coupon)}
                            sx={{ cursor: "pointer", maxWidth: "100%" }}
                          >
                            <SkuCopyRow
                              sku={coupon.code}
                              emptyFallback="—"
                              copyLabel="Copy coupon code"
                              color={swayyColors.deepGreen}
                              fontWeight={700}
                              typographySx={couponCodeSx}
                            />
                          </Box>
                          {coupon.description ? (
                            <CustomTypography
                              variant="body3"
                              color={swayyColors.textSecondary}
                              sx={{
                                fontFamily: swayyFont,
                                fontSize: 12,
                                mt: 0.25,
                              }}
                            >
                              {coupon.description}
                            </CustomTypography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <CustomTypography
                            variant="body2"
                            color={swayyColors.deepGreen}
                            sx={{ fontFamily: swayyFont, fontWeight: 600 }}
                          >
                            {formatSwayyCouponDiscount(coupon)}
                          </CustomTypography>
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <CustomTypography
                            variant="body2"
                            color={swayyColors.deepGreen}
                            sx={{ fontFamily: swayyFont, fontWeight: 500 }}
                          >
                            {getSwayyCouponChannelLabel(coupon.channel)}
                          </CustomTypography>
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <CustomTypography
                            variant="body2"
                            color={swayyColors.deepGreen}
                            sx={{ fontFamily: swayyFont, fontWeight: 600 }}
                          >
                            {formatSwayyCouponUsage(coupon)}
                          </CustomTypography>
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <StatusBadge
                            status={getSwayyCouponDisplayState(coupon)}
                          />
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <ExpiryBadge isExpired={coupon.is_expired} />
                        </TableCell>
                        <TableCell sx={tdSx}>
                          {coupon.starts_at
                            ? formatSwayyCouponDate(coupon.starts_at)
                            : "—"}
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <CouponDateValue value={coupon.ends_at} />
                        </TableCell>
                        <TableCell sx={tdSx}>
                          {coupon.created_at ? formatDate(coupon.created_at) : "—"}
                        </TableCell>
                        {showActions && onEdit ? (
                          <TableCell sx={tdSx}>
                            <CouponRowActionMenu
                              coupon={coupon}
                              onEdit={onEdit}
                            />
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {store.coupons.length > 0 && paginationFooter}
    </Paper>
  );
};

export default observer(SwayyCouponsTable);
